import React, { useCallback, useState } from 'react'
import { Modal } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { AiUploadImageSection } from './AiUploadImageSection'
import { AiUploadDocumentSection } from './AiUploadDocumentSection'
import { AiDataExtractionAnalyzingModal, ProcessingState } from './AiDataExtractionAnalyzingModal'
import { useAiExtractionFlow } from './useAiExtractionFlow'
import { useAiUploadedAttachments } from './useAiUploadedAttachments'
import { AiDataExtractionUploadError } from './AiDataExtractionUploadError'
import { useFeatureSwitch } from '../../../../utils/features/FeatureSwitchContext'
import { FeatureSwitch } from '../../../../utils/features/featureSwitches'

interface AiDataExtractionUploadModalProps {
  extractionApiError?: boolean
  isOpen: boolean
  onClearExtractionApiError?: () => void
  onClose: () => void
  onExtractionFailed?: () => void
  onSuccess?: () => void
}

export const AiDataExtractionUploadModal = ({
  extractionApiError,
  isOpen,
  onClearExtractionApiError,
  onClose,
  onExtractionFailed,
  onSuccess,
}: AiDataExtractionUploadModalProps) => {
  const { t } = useTranslation()
  const { isActive: isAiDataExtractionReentryEnabled } = useFeatureSwitch(
    FeatureSwitch.PPA_12731_AI_DATA_EXTRACTION_REENTRY
  )
  const isExtractionErrorControlled = extractionApiError !== undefined
  const [internalExtractionApiError, setInternalExtractionApiError] = useState(false)
  const showExtractionApiError = isExtractionErrorControlled ? extractionApiError : internalExtractionApiError

  const clearExtractionApiError = useCallback(() => {
    if (isExtractionErrorControlled) {
      onClearExtractionApiError?.()
    } else {
      setInternalExtractionApiError(false)
    }
  }, [isExtractionErrorControlled, onClearExtractionApiError])

  const handleModalClose = useCallback(() => {
    if (!isExtractionErrorControlled) {
      setInternalExtractionApiError(false)
    }
    onClose()
  }, [isExtractionErrorControlled, onClose])

  const {
    documentsMaxFileSizeErrorText,
    energyCertificateDocument,
    energyCertificateErrorText,
    floorPlanDocument,
    floorPlanErrorText,
    getLatestAttachmentsForExtraction,
    hasSelectedFiles,
    imageErrorText,
    images,
    onEnergyCertificateRemove,
    onEnergyCertificateSelect,
    onFloorPlanRemove,
    onFloorPlanSelect,
    onImageRemove,
    onImageReorder,
    onImageSelect,
    waitForPendingUploads,
  } = useAiUploadedAttachments()
  const { isAnalyzingOpen, processingState, retryAnalyzing, startAnalyzing } = useAiExtractionFlow({
    getLatestAttachmentsForExtraction,
    hasSelectedFiles,
    onClearExtractionApiError: clearExtractionApiError,
    onClose,
    onExtractionFailed: () => {
      if (isExtractionErrorControlled) {
        onExtractionFailed?.()
      } else {
        setInternalExtractionApiError(true)
      }
    },
    onSuccess,
    waitForPendingUploads,
  })

  const primaryFooterBtn = (
    <Modal.PrimaryFooterButton
      label={t(
        isAiDataExtractionReentryEnabled
          ? 'wizard.uploadAiBanner.modal.v2.uploadButton'
          : 'wizard.uploadAiBanner.modal.uploadButton'
      )}
      onClick={startAnalyzing}
      disabled={!hasSelectedFiles}
      variant='filledDefault'
    />
  )
  const secondaryFooterBtn = (
    <Modal.SecondaryFooterButton label={t('wizard.uploadAiBanner.modal.cancelButton')} onClick={handleModalClose} />
  )

  return (
    <>
      <Modal.Root isOpen={isOpen && !isAnalyzingOpen} onClose={handleModalClose}>
        <Modal.Backdrop>
          <Modal.Surface width={601}>
            <Modal.Header>
              <Modal.TitleGroup>
                <Modal.Title>
                  {t(
                    isAiDataExtractionReentryEnabled
                      ? 'wizard.uploadAiBanner.modal.v2.title'
                      : 'wizard.uploadAiBanner.modal.title'
                  )}
                </Modal.Title>
              </Modal.TitleGroup>
              <Modal.CloseButton aria-label='close' />
            </Modal.Header>
            <Modal.Body>
              <p className='font-body-large-regular margin-bottom-l'>
                {t(
                  isAiDataExtractionReentryEnabled
                    ? 'wizard.uploadAiBanner.modal.v2.description'
                    : 'wizard.uploadAiBanner.modal.description'
                )}
              </p>
              {showExtractionApiError ? (
                <AiDataExtractionUploadError
                  className='margin-bottom-l'
                  data-testid='ai-extraction-api-error'
                  message={t('wizard.uploadAiBanner.modal.extractionApiError')}
                  role='alert'
                />
              ) : null}
              <AiUploadImageSection
                errorText={imageErrorText}
                images={images}
                onFileSelect={onImageSelect}
                onRemove={onImageRemove}
                onReorder={onImageReorder}
              />
              <AiUploadDocumentSection
                documentsSectionErrorText={documentsMaxFileSizeErrorText}
                energyCertificateErrorText={energyCertificateErrorText}
                floorPlanDocument={floorPlanDocument}
                floorPlanErrorText={floorPlanErrorText}
                energyCertificateDocument={energyCertificateDocument}
                onFloorPlanSelect={onFloorPlanSelect}
                onEnergyCertificateSelect={onEnergyCertificateSelect}
                onFloorPlanRemove={onFloorPlanRemove}
                onEnergyCertificateRemove={onEnergyCertificateRemove}
              />
              <p className='font-body-small-regular margin-bottom-l margin-top-m font-light'>
                <span className={'font-body-small-bold'}>{t('wizard.uploadAiBanner.modal.hint')}</span>
                <span>
                  {t(
                    isAiDataExtractionReentryEnabled
                      ? 'wizard.uploadAiBanner.modal.v2.hintText'
                      : 'wizard.uploadAiBanner.modal.hintText'
                  )}
                </span>
              </p>
            </Modal.Body>
            <Modal.Footer>
              {secondaryFooterBtn}
              {primaryFooterBtn}
            </Modal.Footer>
          </Modal.Surface>
        </Modal.Backdrop>
      </Modal.Root>
      <AiDataExtractionAnalyzingModal
        visible={isAnalyzingOpen}
        state={processingState}
        onRetry={processingState === ProcessingState.Error ? retryAnalyzing : undefined}
      />
    </>
  )
}
